import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import type { FilterOption } from '@/components/molecules'
import { usePetClaimList } from '@/shared/hooks/queries/use-pet-claim-list'
import { usePetPolicyIds } from '@/shared/hooks/queries/use-pet-policy-ids'
import { usePets } from '@/shared/hooks/queries/use-pets'
import { usePolicies } from '@/shared/hooks/queries/use-policies'
import { formatCurrency } from '@/shared/helpers/format-currency'
import { formatDate } from '@/shared/helpers/format-date'
import {
  CENTS_PER_DOLLAR,
  MISSING_FIELDS_SEPARATOR,
} from '@/shared/constants/claims'
import { ROUTES } from '@/shared/constants/routes'
import { PET_CLAIM_LIST_KIND } from '@/shared/types/global.types'
import type { PetClaimListKind } from '@/shared/types/global.types'
import {
  getClaimStatusKey,
  getClaimStatusTone,
} from '@/shared/helpers/claims-status'

const CLAIMS_PER_PAGE = 10

export function useClaimsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [activeKind, setActiveKind] = useState<PetClaimListKind>(
    PET_CLAIM_LIST_KIND.DRAFTS,
  )
  const [page, setPage] = useState(1)

  const { policyIds, isLoading: isPolicyIdsLoading } = usePetPolicyIds()
  const listQuery = usePetClaimList(
    activeKind,
    policyIds,
    page,
    CLAIMS_PER_PAGE,
  )
  const { data: policies = [] } = usePolicies()
  const { data: pets = [] } = usePets()
  const pagination = listQuery.data?.pagination

  // Claims only carry their policy number; the pet comes from that policy
  const claims = useMemo(
    () =>
      (listQuery.data?.data ?? []).map((claim) => {
        const policy = policies.find(
          (item) => String(item.policyExternalId) === String(claim.policy_id),
        )
        const pet = pets.find((item) => item.id === policy?.petId)
        return {
          id: claim.id,
          petName: pet?.name ?? `#${claim.policy_id}`,
          petPictureUrl: pet?.petPictureUrl,
          claimNumber: claim.core_claim_id
            ? `#${claim.core_claim_id}`
            : undefined,
          statusLabel: t(getClaimStatusKey(claim.status), {
            defaultValue: claim.status,
          }),
          statusTone: getClaimStatusTone(claim.status),
          // The API sends null for fields not filled in yet
          amount:
            typeof claim.amount === 'number'
              ? formatCurrency(claim.amount / CENTS_PER_DOLLAR)
              : undefined,
          date: formatDate(claim.created_at),
          message: claim.missing_fields.length
            ? t('claims.detail.missingFields', {
                fields: claim.missing_fields
                  .map((field) => field.label)
                  .join(MISSING_FIELDS_SEPARATOR),
              })
            : undefined,
        }
      }),
    [listQuery.data, policies, pets, t],
  )

  const filters: FilterOption<PetClaimListKind>[] = [
    { key: PET_CLAIM_LIST_KIND.DRAFTS, label: t('claims.list.filters.drafts') },
    { key: PET_CLAIM_LIST_KIND.FILED, label: t('claims.list.filters.filed') },
  ]

  function handleSelectKind(kind: PetClaimListKind) {
    setActiveKind(kind)
    setPage(1)
  }

  function handleOpenClaim(claimId: string) {
    navigate({ to: ROUTES.CLAIM_DETAIL, params: { claimId } })
  }

  function handleFileClaim() {
    navigate({ to: ROUTES.CLAIMS_PET })
  }

  const totalPages = pagination?.total_pages ?? 0

  return {
    claims,
    filters,
    activeKind,
    handleSelectKind,
    isLoading: isPolicyIdsLoading || listQuery.isLoading,
    isFetchingPage: listQuery.isFetching && !listQuery.isLoading,
    isError: listQuery.isError,
    handleRetry: () => listQuery.refetch(),
    page,
    totalPages,
    hasPreviousPage: page > 1,
    hasNextPage: page < totalPages,
    handlePreviousPage: () => setPage((current) => Math.max(current - 1, 1)),
    handleNextPage: () => setPage((current) => current + 1),
    handleOpenClaim,
    handleFileClaim,
  }
}
